import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { skillsData, type Skill } from '@/mocks/skillsData';

function formatDownloads(n: number): string {
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
  return `${n}`;
}

export default function SkillDetailPage() {
  const { skillId } = useParams<{ skillId: string }>();
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const allSkills = skillsData;

  const skill = allSkills.find((s) => s.id === skillId);

  const textPrimary = isDark ? 'text-white' : 'text-[#111]';
  const textSecondary = isDark ? 'text-[#999]' : 'text-[#666]';
  const textMuted = isDark ? 'text-[#666]' : 'text-[#888]';
  const borderLight = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const bgWhite5 = isDark ? 'bg-white/5' : 'bg-black/5';
  const bgWhite10 = isDark ? 'bg-white/10' : 'bg-black/10';

  if (!skill) {
    return (
      <div className={`relative min-h-screen overflow-x-hidden ${isDark ? 'bg-[#050505] text-white' : 'bg-[#f5f5f5] text-[#111]'}`}>
        <Navbar />
        <main className="relative z-10 pt-32 pb-16 text-center">
          <i className="ri-error-warning-line w-12 h-12 flex items-center justify-center mx-auto mb-4 text-4xl text-[#ff4444]/50" />
          <h1 className={`text-2xl font-bold mb-2 ${textPrimary}`}>{t('skills.notFound', 'Skill not found')}</h1>
          <p className={`text-sm mb-6 ${textSecondary}`}>{t('skills.notFoundDesc', 'The skill you are looking for does not exist.')}</p>
          <Link
            to="/skills"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#00ff88]/10 border border-[#00ff88]/30 text-[#00ff88] text-sm font-medium rounded-lg hover:bg-[#00ff88]/20 transition-all"
          >
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center" />
            {t('skills.backToSkills', 'Back to Skills')}
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedSkills = allSkills
    .filter((s) => s.id !== skill.id && s.category === skill.category)
    .slice(0, 3);

  const handleCopy = () => {
    if (skill.installCommand) {
      navigator.clipboard.writeText(skill.installCommand);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className={`relative min-h-screen overflow-x-hidden ${isDark ? 'bg-[#050505] text-white' : 'bg-[#f5f5f5] text-[#111]'}`}>
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className={`absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full blur-[150px] ${isDark ? 'bg-[#00ff88]/[0.02]' : 'bg-[#00ff88]/[0.04]'}`} />
      </div>

      <Navbar />

      <main className="relative z-10 pt-24 md:pt-32 pb-16">
        <div className="w-full px-4 md:px-8 lg:px-12">
          {/* Breadcrumb */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="flex items-center gap-2 text-xs mb-8"
          >
            <Link to="/" className={`${textMuted} hover:text-[#00ff88] transition-colors`}>
              {t('layout.backToHome', 'Home')}
            </Link>
            <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center text-[#00ff88]" />
            <Link to="/skills" className={`${textMuted} hover:text-[#00ff88] transition-colors`}>
              {t('nav.skills', 'Skills')}
            </Link>
            <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center text-[#00ff88]" />
            <span className={textSecondary}>{skill.name}</span>
          </motion.div>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col md:flex-row md:items-start gap-6 mb-10"
          >
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <h1 className={`text-2xl md:text-3xl font-bold ${textPrimary}`}>{skill.name}</h1>
                <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-mono uppercase tracking-wider border ${
                  skill.tag === 'Plugin'
                    ? 'border-[#00ccff]/30 text-[#00ccff] bg-[#00ccff]/10'
                    : 'border-[#00ff88]/30 text-[#00ff88] bg-[#00ff88]/10'
                }`}>
                  {skill.tag}
                </span>
              </div>
              <p className={`text-sm mb-4 ${textMuted}`}>
                by <span className={textSecondary}>{skill.author}</span>
              </p>
              <p className={`text-base leading-relaxed max-w-2xl ${textSecondary}`}>
                {skill.description}
              </p>

              <div className="flex flex-wrap items-center gap-4 mt-5">
                <span className={`flex items-center gap-1.5 text-xs font-mono ${textMuted}`}>
                  <i className="ri-star-line w-3 h-3 flex items-center justify-center" />
                  {skill.stars} stars
                </span>
                <span className={`flex items-center gap-1.5 text-xs font-mono ${textMuted}`}>
                  <i className="ri-download-line w-3 h-3 flex items-center justify-center" />
                  {formatDownloads(skill.downloads)} downloads
                </span>
                {skill.version && (
                  <span className={`flex items-center gap-1.5 text-xs font-mono ${textMuted}`}>
                    <i className="ri-git-branch-line w-3 h-3 flex items-center justify-center" />
                    {skill.version}
                  </span>
                )}
              </div>
            </div>

            {/* Install button */}
            <div className="flex-shrink-0">
              <button
                onClick={handleCopy}
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#00ff88] text-[#050505] font-semibold text-sm rounded-lg hover:bg-[#00cc6a] transition-colors whitespace-nowrap"
              >
                <i className="ri-download-line w-4 h-4 flex items-center justify-center" />
                {t('skills.installNow', 'Install')}
              </button>
            </div>
          </motion.div>

          {/* Install Command */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className={`rounded-xl border p-5 mb-10 ${borderLight} ${bgWhite5}`}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className={`text-sm font-semibold ${textPrimary}`}>{t('skills.installCommand', 'Install Command')}</h3>
              <button
                onClick={handleCopy}
                className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-all ${borderLight} ${textSecondary} hover:border-[#00ff88]/30 hover:text-[#00ff88]`}
              >
                <i className={`${copied ? 'ri-check-line' : 'ri-file-copy-line'} w-3 h-3 flex items-center justify-center`} />
                {copied ? t('skills.copied', 'Copied!') : t('skills.copy', 'Copy')}
              </button>
            </div>
            <div className="code-block p-4 overflow-x-auto">
              <code className={`text-sm font-mono ${textSecondary}`}>
                {skill.installCommand}
              </code>
            </div>
          </motion.div>

          {/* Description + Usage */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
            {/* Main content */}
            <div className="lg:col-span-2 space-y-6">
              {skill.detailedDescription && (
                <div className={`rounded-xl border p-5 ${borderLight} ${bgWhite5}`}>
                  <h3 className={`text-sm font-semibold mb-3 ${textPrimary}`}>{t('skills.about', 'About')}</h3>
                  <p className={`text-sm leading-relaxed ${textSecondary}`}>
                    {skill.detailedDescription}
                  </p>
                </div>
              )}

              {skill.usageExample && (
                <div className={`rounded-xl border p-5 ${borderLight} ${bgWhite5}`}>
                  <h3 className={`text-sm font-semibold mb-3 ${textPrimary}`}>{t('skills.usageExample', 'Usage Example')}</h3>
                  <div className="code-block p-4 overflow-x-auto">
                    <pre className={`text-xs font-mono leading-relaxed ${textSecondary}`}>
                      <code>{skill.usageExample}</code>
                    </pre>
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar info */}
            <div className="space-y-4">
              <div className={`rounded-xl border p-5 ${borderLight} ${bgWhite5}`}>
                <h3 className={`text-sm font-semibold mb-3 ${textPrimary}`}>{t('skills.info', 'Information')}</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className={textMuted}>Author</span>
                    <span className={textSecondary}>{skill.author}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className={textMuted}>Category</span>
                    <span className={textSecondary}>{skill.category}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className={textMuted}>Type</span>
                    <span className={textSecondary}>{skill.tag}</span>
                  </div>
                  {skill.version && (
                    <div className="flex items-center justify-between text-sm">
                      <span className={textMuted}>Version</span>
                      <span className={textSecondary}>{skill.version}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-sm">
                    <span className={textMuted}>Downloads</span>
                    <span className={textSecondary}>{formatDownloads(skill.downloads)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className={textMuted}>Stars</span>
                    <span className={textSecondary}>{skill.stars}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Related Skills */}
          {relatedSkills.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <h2 className={`text-lg font-semibold mb-4 ${textPrimary}`}>
                {t('skills.related', 'Related Skills')}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {relatedSkills.map((rs) => (
                  <Link
                    key={rs.id}
                    to={`/skills/${rs.id}`}
                    className={`glass-card-hover p-4 group block`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className={`text-sm font-semibold ${textPrimary} group-hover:text-[#00ff88] transition-colors`}>
                        {rs.name}
                      </h3>
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono uppercase border ${
                        rs.tag === 'Plugin'
                          ? 'border-[#00ccff]/30 text-[#00ccff] bg-[#00ccff]/10'
                          : 'border-[#00ff88]/30 text-[#00ff88] bg-[#00ff88]/10'
                      }`}>
                        {rs.tag}
                      </span>
                    </div>
                    <p className={`text-xs line-clamp-2 ${textSecondary}`}>{rs.description}</p>
                    <div className={`flex items-center gap-3 mt-3 text-[10px] font-mono ${textMuted}`}>
                      <span className="flex items-center gap-1">
                        <i className="ri-star-line w-3 h-3 flex items-center justify-center" />
                        {rs.stars}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-download-line w-3 h-3 flex items-center justify-center" />
                        {formatDownloads(rs.downloads)}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}